import {
  Box,
  Button,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import React, { useEffect, useState } from 'react'
import { styles } from '../proTab/styles'
import { getOrderFromStack, setStackSort } from './columnSortUtils'
import { SHARE_LINK_SORT_DEFAULT_LABEL } from './performanceSortConfig'

export type ShareLinkSortField =
  | 'productName'
  | 'shortUrl'
  | 'createdAt'
  | 'clicksCount'
  | 'earnings'

export type ShareLinkColumnSortOrder = 'off' | 'asc' | 'desc'

export type ShareLinkColumnSort = {
  field: ShareLinkSortField
  order: ShareLinkColumnSortOrder
}

export const SHARE_LINK_TABLE_SORT_COLUMNS: {
  value: ShareLinkSortField
  label: string
}[] = [
  { value: 'productName', label: 'item' },
  { value: 'shortUrl', label: 'link' },
  { value: 'createdAt', label: 'created' },
  { value: 'clicksCount', label: 'total clicks' },
  { value: 'earnings', label: 'total earned' },
]

export const DEFAULT_SHARE_LINK_SORT_STACK: ShareLinkSortCriterion[] = []

export type ShareLinkSortState = {
  sortStack: ShareLinkSortCriterion[]
}

export type ShareLinkSortCriterion = {
  field: ShareLinkSortField
  order: 'asc' | 'desc'
}

export function shareLinkSortStackForApi(
  sortStack: ShareLinkSortCriterion[]
): ShareLinkSortCriterion[] {
  return sortStack
}

export function isDefaultShareLinkColumnSorts(
  sortStack: ShareLinkSortCriterion[]
): boolean {
  if (sortStack.length !== DEFAULT_SHARE_LINK_SORT_STACK.length) {
    return false
  }
  return sortStack.every(
    (s, i) =>
      s.field === DEFAULT_SHARE_LINK_SORT_STACK[i].field &&
      s.order === DEFAULT_SHARE_LINK_SORT_STACK[i].order
  )
}

function orderShort(order: 'asc' | 'desc'): string {
  return order === 'asc' ? '↑' : '↓'
}

export function shareLinkSortTriggerLabel(sort: ShareLinkSortState): string {
  const active = shareLinkSortStackForApi(sort.sortStack)
  if (active.length === 0) {
    return SHARE_LINK_SORT_DEFAULT_LABEL
  }
  return active
    .map(
      s =>
        `${SHARE_LINK_TABLE_SORT_COLUMNS.find(c => c.value === s.field)
          ?.label ?? s.field} ${orderShort(s.order)}`
    )
    .join(' · ')
}

type ShareLinkSortMenuProps = {
  sort: ShareLinkSortState
  /** Called only when the user clicks done. */
  onChange: (sortStack: ShareLinkSortCriterion[]) => void
}

const ShareLinkSortMenu = ({ sort, onChange }: ShareLinkSortMenuProps) => {
  const classes = styles()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const [draftStack, setDraftStack] = useState(sort.sortStack)

  useEffect(() => {
    if (!open) {
      setDraftStack(sort.sortStack)
    }
  }, [sort.sortStack, open])

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDraftStack(sort.sortStack.map(s => ({ ...s })))
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleDone = () => {
    onChange(draftStack.map(s => ({ ...s })))
    setAnchorEl(null)
  }

  const displaySort = open ? { sortStack: draftStack } : sort

  const selectMenuProps = {
    disablePortal: true,
    getContentAnchorEl: null,
    anchorOrigin: { vertical: 'bottom' as const, horizontal: 'left' as const },
    transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
  }

  const setColumnOrder = (
    field: ShareLinkSortField,
    order: ShareLinkColumnSortOrder
  ) => {
    setDraftStack(prev => setStackSort(prev, field, order))
  }

  return (
    <>
      <Button
        type="button"
        className={classes.sortMenuTrigger}
        onClick={handleOpen}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? 'share-link-sort-menu' : undefined}
        endIcon={<ArrowDropDownIcon />}
      >
        <span className={classes.sortMenuTriggerLabel}>
          {shareLinkSortTriggerLabel(displaySort)}
        </span>
      </Button>
      <Menu
        id="share-link-sort-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ className: classes.sortMenuPaper }}
        MenuListProps={{ className: classes.sortMenuList }}
      >
        <Box
          className={classes.sortMenuContent}
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
        >
          <Typography component="p" className={classes.sortMenuHeading}>
            sort by column
          </Typography>
          {SHARE_LINK_TABLE_SORT_COLUMNS.map(col => {
            const order = getOrderFromStack(col.value, draftStack)
            return (
              <Box key={col.value} className={classes.sortMenuColumnRow}>
                <Typography
                  component="span"
                  className={classes.sortMenuColumnLabel}
                >
                  {col.label}
                </Typography>
                <TextField
                  className={classes.sortMenuColumnOrder}
                  select
                  value={order}
                  onChange={e =>
                    setColumnOrder(
                      col.value,
                      e.target.value as ShareLinkColumnSortOrder
                    )
                  }
                  variant="outlined"
                  size="small"
                  SelectProps={{ MenuProps: selectMenuProps }}
                  inputProps={{ 'aria-label': `sort ${col.label}` }}
                >
                  <MenuItem value="off">off</MenuItem>
                  <MenuItem value="asc">ascending</MenuItem>
                  <MenuItem value="desc">descending</MenuItem>
                </TextField>
              </Box>
            )
          })}
          <Button
            type="button"
            className={classes.sortMenuDoneButton}
            onClick={handleDone}
            fullWidth
          >
            <Typography variant="button">done</Typography>
          </Button>
        </Box>
      </Menu>
    </>
  )
}

export default ShareLinkSortMenu
